/**
 * Signature Extraction Component
 * Component for extracting and viewing signatures from signed certificates
 */

import React, { useState, useEffect, useRef } from "react";
import type { CertificateApplication } from "../types/certificate.types";
import BusyOverlay from "../components/common/BusyOverlay";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import { getFullPath } from "../utils/getFullPath";
import { apiRequest } from "../api/apiClient";
import "../styles/pages/Users.css";

interface SignatureExtractionProps {
  application: CertificateApplication;
  documentPath: string;
  onClose: () => void;
}

interface SignatureExtractionRecord {
  id: number;
  certificateApplicationId: number;
  signatureType: string;
  signaturePath: string;
  createdBy?: number;
  createdByName?: string;
  createdAt: string;
}

interface SelectionArea {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export const SignatureExtraction: React.FC<SignatureExtractionProps> = ({
  application,
  documentPath,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [signatureRecords, setSignatureRecords] = useState<
    SignatureExtractionRecord[]
  >([]);
  const [selectedSignature, setSelectedSignature] = useState<string | null>(
    null,
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionArea, setSelectionArea] = useState<SelectionArea | null>(
    null,
  );
  const [showPopupMenu, setShowPopupMenu] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [capturing, setCapturing] = useState(false);
  const [deletingSignatureId, setDeletingSignatureId] = useState<number | null>(
    null,
  );

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const signatureTypes = application.processAtHeadquarters
    ? ["Groom", "Bride", "Groom Parent", "Bride Parent"]
    : [
        "Groom",
        "Bride",
        "Groom Parent",
        "Bride Parent",
        "Paramount Chief",
        "County City Mayor",
        "County Superintendent",
      ];

  useEffect(() => {
    fetchSignatures();
  }, [application]);

  // Auto-select the first signature type that hasn't been captured yet
  useEffect(() => {
    if (signatureRecords.length === 0) {
      // No signatures captured yet, select the first type
      setSelectedSignature(signatureTypes[0]);
    } else {
      // Find the first signature type that doesn't exist in signatureRecords
      const capturedTypes = signatureRecords.map((sig) => sig.signatureType);
      const firstMissingType = signatureTypes.find(
        (type) => !capturedTypes.includes(type),
      );

      if (firstMissingType) {
        setSelectedSignature(firstMissingType);
      } else {
        // All signatures captured, deselect
        setSelectedSignature(null);
      }
    }
  }, [signatureRecords, signatureTypes]);

  const fetchSignatures = async (): Promise<SignatureExtractionRecord[]> => {
    try {
      setLoading(true);
      console.log(
        "Fetching signatures for certificate application:",
        application.id,
      );

      const response = await apiRequest(
        `/CertificateSignatures/by-certificate-application/${application.id}`,
        {
          method: "GET",
        },
      );

      console.log("Fetch signatures response status:", response.status);

      if (!response.ok) {
        // No signatures yet, that's okay
        console.log("No signatures found or error, setting empty array");
        setSignatureRecords([]);
        return [];
      }

      const data = await response.json();
      console.log("Signature records received:", data);

      const records = Array.isArray(data) ? data : [];
      setSignatureRecords(records);
      return records;
    } catch (err: any) {
      console.error("Error fetching signatures:", err);
      setSignatureRecords([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getSignatureImage = (signatureType: string): string | null => {
    const signature = signatureRecords.find(
      (sig) => sig.signatureType === signatureType,
    );

    if (signature && signature.signaturePath) {
      const imageUrl = getFullPath(signature.signaturePath);
      console.log(`Signature image for ${signatureType}:`, {
        path: signature.signaturePath,
        url: imageUrl,
      });
      return imageUrl;
    }

    return null;
  };

  const getSignatureRecord = (
    signatureType: string,
  ): SignatureExtractionRecord | undefined => {
    return signatureRecords.find((sig) => sig.signatureType === signatureType);
  };

  const handleDeleteSignature = async (signatureType: string) => {
    const existingSignature = getSignatureRecord(signatureType);

    if (!existingSignature?.id) {
      return;
    }

    const confirmed = window.confirm(
      `Delete ${signatureType} signature? This will allow recapturing it.`,
    );
    if (!confirmed) {
      return;
    }

    try {
      setDeletingSignatureId(existingSignature.id);

      const response = await apiRequest(
        `/CertificateSignatures/${existingSignature.id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }

      showSuccessToast(`${signatureType} signature deleted successfully`);
      await fetchSignatures();
      setSelectedSignature(signatureType);
      setSelectionArea(null);
      setShowPopupMenu(false);
    } catch (err) {
      console.error("Error deleting signature:", err);
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setDeletingSignatureId(null);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectedSignature || !containerRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsSelecting(true);
    setSelectionArea({ startX: x, startY: y, endX: x, endY: y });
    setShowPopupMenu(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !selectionArea || !containerRef.current) return;

    e.preventDefault();

    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

    setSelectionArea({ ...selectionArea, endX: x, endY: y });
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !selectionArea || !containerRef.current) return;

    e.preventDefault();
    setIsSelecting(false);

    // Check if selection area is significant (at least 10x10 pixels)
    const width = Math.abs(selectionArea.endX - selectionArea.startX);
    const height = Math.abs(selectionArea.endY - selectionArea.startY);

    if (width > 10 && height > 10) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

      setPopupPosition({ x, y });
      setShowPopupMenu(true);
    } else {
      setSelectionArea(null);
    }
  };

  const handleCapture = async () => {
    if (!selectionArea || !selectedSignature || !imageRef.current) {
      console.error("Missing required data:", {
        selectionArea,
        selectedSignature,
        imageRef: imageRef.current,
      });
      return;
    }

    if (!application.id) {
      console.error("Application ID is missing");
      showErrorToast("Application ID is required");
      return;
    }

    // Fetch latest signatures before capture so recapture can replace existing records.
    console.log("Fetching latest signatures before capture...");
    const latestSignatures = await fetchSignatures();

    try {
      setCapturing(true);
      setShowPopupMenu(false);

      const img = imageRef.current;
      const canvas = document.createElement("canvas");

      // Calculate actual image coordinates (accounting for display size vs actual size)
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;

      const x = Math.min(selectionArea.startX, selectionArea.endX) * scaleX;
      const y = Math.min(selectionArea.startY, selectionArea.endY) * scaleY;
      const width =
        Math.abs(selectionArea.endX - selectionArea.startX) * scaleX;
      const height =
        Math.abs(selectionArea.endY - selectionArea.startY) * scaleY;

      // High density multiplier for better quality (2x DPI)
      const densityMultiplier = 2;
      const highDensityWidth = width * densityMultiplier;
      const highDensityHeight = height * densityMultiplier;

      console.log("Capture dimensions:", {
        x,
        y,
        width,
        height,
        scaleX,
        scaleY,
        densityMultiplier,
        finalWidth: highDensityWidth,
        finalHeight: highDensityHeight,
      });

      // Set canvas to high density dimensions
      canvas.width = highDensityWidth;
      canvas.height = highDensityHeight;

      const ctx = canvas.getContext("2d", {
        alpha: true,
        desynchronized: false,
        willReadFrequently: false,
      });
      if (!ctx) {
        throw new Error("Could not get canvas context");
      }

      // Enable high-quality image rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Draw the selected portion of the image at high density
      ctx.drawImage(
        img,
        x,
        y,
        width,
        height,
        0,
        0,
        highDensityWidth,
        highDensityHeight,
      );

      // Process image to make background transparent
      const imageData = ctx.getImageData(
        0,
        0,
        highDensityWidth,
        highDensityHeight,
      );
      const data = imageData.data;

      // Threshold for transparency (adjust this value: 0-255, higher = more aggressive)
      // Pixels brighter than this will become transparent
      const brightnessThreshold = 240;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // Calculate brightness using luminance formula
        const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

        // If pixel is bright (white/light background), make it transparent
        if (brightness > brightnessThreshold) {
          // Gradually fade based on how close to white
          const alpha = Math.max(
            0,
            ((255 - brightness) / (255 - brightnessThreshold)) * 255,
          );
          data[i + 3] = alpha; // Set alpha channel
        }
      }

      // Put the modified image data back to canvas
      ctx.putImageData(imageData, 0, 0);

      console.log("Applied transparent background to signature");

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) {
            console.log("Blob created:", b.size, "bytes");
            resolve(b);
          } else {
            reject(new Error("Failed to create blob"));
          }
        }, "image/png");
      });

      // Create FormData and send to backend
      const formData = new FormData();
      const fileName = `${selectedSignature.replace(/\s+/g, "_")}-signature.png`;
      formData.append("file", blob, fileName);
      formData.append("signatureType", selectedSignature);
      formData.append("certificateApplicationId", application.id!.toString());

      const existingSignature = latestSignatures.find(
        (sig) => sig.signatureType === selectedSignature,
      );
      const shouldUpdateExisting = Boolean(existingSignature?.id);
      const requestMethod = shouldUpdateExisting ? "PUT" : "POST";
      const requestPath = shouldUpdateExisting
        ? `/CertificateSignatures/${existingSignature!.id}`
        : "/CertificateSignatures";

      // Console log POST request body for debugging
      console.log(
        `=== ${requestMethod} Request to ${requestPath} (recapture supported) ===`,
      );
      console.log("Request Body:", {
        certificateApplicationId: application.id,
        signatureType: selectedSignature,
        operation: shouldUpdateExisting ? "update-existing" : "create-new",
        existingSignatureId: existingSignature?.id,
        file: {
          name: fileName,
          size: blob.size,
          type: blob.type,
        },
      });
      console.log("FormData entries:");
      for (const [key, value] of formData.entries()) {
        if (value instanceof Blob) {
          console.log(`  ${key}:`, {
            name: (value as File).name || fileName,
            size: value.size,
            type: value.type,
          });
        } else {
          console.log(`  ${key}:`, value);
        }
      }
      console.log("===================================================");

      let response = await apiRequest(requestPath, {
        method: requestMethod,
        body: formData,
      });

      // Fallback for APIs that only support POST upsert behavior.
      if (!response.ok && shouldUpdateExisting) {
        console.warn(
          `Primary update request failed (${response.status}). Falling back to POST /CertificateSignatures.`,
        );
        response = await apiRequest("/CertificateSignatures", {
          method: "POST",
          body: formData,
        });
      }

      console.log(
        `${requestMethod} Response Status:`,
        response.status,
        response.statusText,
      );

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          console.error("Capture error response:", errorData);
          errorMessage =
            errorData.message ||
            errorData.title ||
            errorData.error ||
            errorMessage;
        } else {
          const errorText = await response.text();
          console.error("Capture error text:", errorText);
          if (errorText) errorMessage = errorText;
        }

        throw new Error(errorMessage);
      }

      // Parse the successful response
      const responseContentType = response.headers.get("content-type") || "";
      const result = responseContentType.includes("application/json")
        ? await response.json()
        : null;
      console.log("CertificateSignatures response body:", result);

      showSuccessToast(
        `${selectedSignature} signature ${shouldUpdateExisting ? "updated" : "captured"} successfully`,
      );

      // Refresh signatures to show the newly captured one
      console.log("Refreshing signatures...");
      await fetchSignatures();

      // Clear selection and deselect
      setSelectionArea(null);
    } catch (err: any) {
      console.error("Error capturing signature:", err);
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setCapturing(false);
    }
  };

  const handleCancelSelection = () => {
    setShowPopupMenu(false);
    setSelectionArea(null);
  };

  const documentUrl = getFullPath(documentPath);

  return (
    <div className="modal-overlay modal-overlay--side-panel">
      <div
        className="modal-content modal-content--side-panel"
        style={{ maxWidth: "95vw", width: "1400px" }}
      >
        <BusyOverlay isVisible={loading} message="Loading..." />

        <div className="modal-header">
          <h2 className="modal-title">Signature Extraction</h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <div
          className="modal-form"
          style={{ maxHeight: "100vh", overflowY: "auto" }}
        >
          {/* Signature Table */}
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                backgroundColor: "#ffffff",
                border: "1px solid #dee2e6",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      border: "1px solid #dee2e6",
                      padding: "12px",
                      width: "120px",
                      textAlign: "center",
                      backgroundColor: "#2c3e50",
                      color: "white",
                      fontWeight: "600",
                    }}
                  >
                    Signature Image
                  </th>
                  <th
                    style={{
                      border: "1px solid #dee2e6",
                      padding: "12px",
                      textAlign: "center",
                      backgroundColor: "#2c3e50",
                      color: "white",
                      fontWeight: "600",
                    }}
                  >
                    Document Preview
                  </th>
                </tr>
              </thead>
              <tbody>
                {signatureTypes.map((type, index) => (
                  <tr key={type}>
                    <td
                      onClick={() => setSelectedSignature(type)}
                      style={{
                        border: "1px solid #dee2e6",
                        padding: "8px",
                        width: "120px",
                        textAlign: "center",
                        verticalAlign: "top",
                        backgroundColor:
                          selectedSignature === type
                            ? "#d4edda"
                            : "transparent",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: "90px",
                            height: "90px",
                          }}
                        >
                          {getSignatureImage(type) ? (
                            <>
                              <img
                                src={
                                  getFullPath(getSignatureImage(type)) ||
                                  undefined
                                }
                                alt={`${type} Signature`}
                                style={{
                                  width: "90px",
                                  height: "90px",
                                  objectFit: "contain",
                                  border: "1px solid #dee2e6",
                                  borderRadius: "4px",
                                  display: "block",
                                }}
                              />
                              <button
                                type="button"
                                title="Delete signature"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSignature(type);
                                }}
                                disabled={
                                  deletingSignatureId ===
                                  getSignatureRecord(type)?.id
                                }
                                style={{
                                  position: "absolute",
                                  left: "4px",
                                  bottom: "4px",
                                  width: "24px",
                                  height: "24px",
                                  border: "none",
                                  borderRadius: "50%",
                                  background:
                                    deletingSignatureId ===
                                    getSignatureRecord(type)?.id
                                      ? "rgba(108,117,125,0.9)"
                                      : "rgba(220,53,69,0.95)",
                                  color: "#fff",
                                  cursor:
                                    deletingSignatureId ===
                                    getSignatureRecord(type)?.id
                                      ? "not-allowed"
                                      : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "12px",
                                  boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                                }}
                              >
                                {deletingSignatureId ===
                                getSignatureRecord(type)?.id
                                  ? "..."
                                  : "🗑"}
                              </button>
                            </>
                          ) : (
                            <div
                              style={{
                                width: "90px",
                                height: "90px",
                                border: "2px dashed #999",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                color: "#999",
                                borderRadius: "4px",
                              }}
                            >
                              No signature
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            fontWeight: "600",
                            color: getSignatureImage(type)
                              ? "#2c3e50"
                              : "#6c757d",
                            textAlign: "center",
                            lineHeight: "1.2",
                            maxWidth: "100px",
                            wordWrap: "break-word",
                          }}
                        >
                          {type}
                        </div>
                      </div>
                    </td>
                    {index === 0 && (
                      <td
                        rowSpan={signatureTypes.length}
                        style={{
                          border: "1px solid #dee2e6",
                          padding: "12px",
                          textAlign: "center",
                          verticalAlign: "top",
                          position: "relative",
                        }}
                      >
                        {documentUrl ? (
                          documentPath.toLowerCase().endsWith(".pdf") ? (
                            <div
                              style={{
                                padding: "1rem",
                                background: "#fff3cd",
                                border: "1px solid #ffc107",
                                borderRadius: "4px",
                                color: "#856404",
                                fontSize: "0.875rem",
                              }}
                            >
                              ⚠️ PDF documents cannot be used for area
                              selection. Please upload an image format (PNG,
                              JPG) for signature extraction.
                            </div>
                          ) : (
                            <div
                              ref={containerRef}
                              onMouseDown={handleMouseDown}
                              onMouseMove={handleMouseMove}
                              onMouseUp={handleMouseUp}
                              style={{
                                position: "relative",
                                cursor: selectedSignature
                                  ? "crosshair"
                                  : "default",
                                userSelect: "none",
                              }}
                            >
                              <img
                                ref={imageRef}
                                src={documentUrl || undefined}
                                alt="Document Preview"
                                crossOrigin="anonymous"
                                style={{
                                  width: "100%",
                                  maxHeight: "900px",
                                  objectFit: "contain",
                                  display: "block",
                                }}
                              />

                              {/* Selection Rectangle */}
                              {selectionArea && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: Math.min(
                                      selectionArea.startX,
                                      selectionArea.endX,
                                    ),
                                    top: Math.min(
                                      selectionArea.startY,
                                      selectionArea.endY,
                                    ),
                                    width: Math.abs(
                                      selectionArea.endX - selectionArea.startX,
                                    ),
                                    height: Math.abs(
                                      selectionArea.endY - selectionArea.startY,
                                    ),
                                    border: "2px dashed #007bff",
                                    backgroundColor: "rgba(0, 123, 255, 0.1)",
                                    pointerEvents: "none",
                                    zIndex: 10,
                                  }}
                                />
                              )}

                              {/* Popup Menu */}
                              {showPopupMenu && selectionArea && (
                                <div
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onMouseMove={(e) => e.stopPropagation()}
                                  onMouseUp={(e) => e.stopPropagation()}
                                  style={{
                                    position: "absolute",
                                    left: popupPosition.x,
                                    top: popupPosition.y,
                                    background: "white",
                                    border: "1px solid #dee2e6",
                                    borderRadius: "4px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                    zIndex: 1000,
                                    minWidth: "120px",
                                  }}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCapture();
                                    }}
                                    disabled={capturing}
                                    style={{
                                      width: "100%",
                                      padding: "0.75rem 1rem",
                                      border: "none",
                                      background: "transparent",
                                      textAlign: "left",
                                      cursor: capturing
                                        ? "not-allowed"
                                        : "pointer",
                                      fontSize: "0.875rem",
                                      color: capturing ? "#6c757d" : "#28a745",
                                      fontWeight: "500",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!capturing)
                                        e.currentTarget.style.background =
                                          "#f8f9fa";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background =
                                        "transparent";
                                    }}
                                  >
                                    {capturing ? (
                                      <>
                                        <span className="spinner spinner--sm"></span>
                                        Capturing...
                                      </>
                                    ) : (
                                      <>
                                        <span>✅</span>
                                        Capture
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelSelection();
                                    }}
                                    disabled={capturing}
                                    style={{
                                      width: "100%",
                                      padding: "0.75rem 1rem",
                                      border: "none",
                                      borderTop: "1px solid #dee2e6",
                                      background: "transparent",
                                      textAlign: "left",
                                      cursor: capturing
                                        ? "not-allowed"
                                        : "pointer",
                                      fontSize: "0.875rem",
                                      color: "#dc3545",
                                      fontWeight: "500",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!capturing)
                                        e.currentTarget.style.background =
                                          "#f8f9fa";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background =
                                        "transparent";
                                    }}
                                  >
                                    <span>❌</span>
                                    Cancel
                                  </button>
                                </div>
                              )}

                              {/* Instruction Text */}
                              {selectedSignature && !selectionArea && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "10px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    background: "rgba(0, 123, 255, 0.9)",
                                    color: "white",
                                    padding: "0.5rem 1rem",
                                    borderRadius: "4px",
                                    fontSize: "0.875rem",
                                    fontWeight: "500",
                                    pointerEvents: "none",
                                  }}
                                >
                                  Click and drag to select {selectedSignature}{" "}
                                  signature area
                                </div>
                              )}
                            </div>
                          )
                        ) : (
                          <div
                            style={{
                              padding: "2rem",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            No document available
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="modal-actions" style={{ marginTop: "1rem" }}>
          <button
            type="button"
            className="btn btn--outline"
            onClick={onClose}
            disabled={loading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureExtraction;
